/**
 * kbContentsService — accesso dati della biblioteca unificata `kb_contents`
 * dal lato di chi SCRIVE (Operator Console, super_admin).
 *
 * Divisione del lavoro, deliberata:
 *  - LETTURE dirette a Firestore (la console è solo super_admin e le rules
 *    bloccano chiunque altro), come `videoDocsService` e `platformCrmService`;
 *  - SCRITTURE del contenuto SOLO via callable. Il corpo vive in
 *    `kb_contents/{slug}/kb_revisioni/{numero}` con `allow write: if false`:
 *    non è una preferenza di stile, è l'unica strada che il database consente.
 *    `pubblicaKbContenuto` è inoltre l'unico modo di arrivare a `pubblicato`,
 *    perché è dove `validaContenuto(..., { perPubblicazione: true })` viene
 *    invocata per davvero.
 *
 * La logica di dominio (validazione, slug, versioni, audience, evidenza) NON si
 * riscrive qui: vive nei moduli puri `shared/kb/*` e si importa. Se la console
 * calcolasse a modo suo, l'editore vedrebbe un pubblico e la Cloud Function ne
 * scriverebbe un altro.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytesResumable, type UploadTask } from 'firebase/storage';
import { db, functions, storage } from '../firebase';
import { listTenants } from './tenantService';
// Moduli PURI condivisi: stessa matematica in console, Cloud Functions e app.
import { aMillis } from '@shared/kb/kbTypes';
import { KB_GOVERNANCE_DEFAULT } from '@shared/kb/kbContentModel';
import { contaDestinatari } from '@shared/kb/kbAudience';
import { idRevisione } from '@shared/kb/kbVersioning';
import { risolviRuoloSulTenant, type UtenteTenantLike } from '@shared/admin/utentiPerTenant';
import { hasCapability } from '@shared/capabilities/capabilityService';
import type {
  KbArea,
  KbContenuto,
  KbFormato,
  KbGovernance,
  KbRevisione,
} from '@shared/kb/kbTypes';
import {
  KB_FILTRO_TUTTI,
  type ArchiviaKbRequest,
  type ArchiviaKbResponse,
  type KbAnteprimaDestinatari,
  type KbCorpoCaricato,
  type KbFiltriContenuti,
  type KbGovernanceCaricata,
  type KbImmagineCaricata,
  type KbPlatea,
  type KbRevisioneConsole,
  type KbTenantConsole,
  type KbUtenteConsole,
  type PubblicaKbRequest,
  type PubblicaKbResponse,
  type SalvaBozzaKbRequest,
  type SalvaBozzaKbResponse,
} from '../types/kb';

const CONTENTS_COLLECTION = 'kb_contents';
const REVISIONI_SUBCOLLECTION = 'kb_revisioni';
const AREAS_COLLECTION = 'kb_areas';
const SETTINGS_COLLECTION = 'kb_settings';
const GOVERNANCE_DOC = 'governance';
const APP_USERS_COLLECTION = 'app_users';
/** Radice Storage delle immagini del corpo: `kb_media/{content_id}/{file}`. */
const STORAGE_ROOT = 'kb_media';

// ============================================================================
// HELPER — regola di progetto: mai whitelist di campi, sempre spread + id
// ============================================================================

function mapSnap<T>(d: QueryDocumentSnapshot<DocumentData>): T {
  return { ...(d.data() as Record<string, unknown>), id: d.id } as T;
}

/** Rimuove le chiavi `undefined` (Firestore e le callable le rifiutano). */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}

function testoNormalizzato(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

// ============================================================================
// CONTENUTI — testate
// ============================================================================

/**
 * Tutte le testate di `kb_contents`, ordinate dalla più recente.
 *
 * Lettura dell'intera collezione, e non una query filtrata, per una ragione
 * misurabile: in fase 1 i documenti sono decine, e ogni combinazione
 * `where(formato) + orderBy(ordinamento_at)` pretenderebbe un indice composito
 * in più. Il filtro sta in memoria (`filtraContenuti`), l'ordinamento è
 * deterministico e nessuna schermata dipende da un indice da deployare.
 * Quando i contenuti diventeranno migliaia questa funzione va sostituita da
 * query paginate: è scritto qui perché la sostituzione sia una decisione, non
 * una scoperta.
 *
 * Le testate NON contengono il corpo (vive nelle revisioni): listarne 50 costa
 * 50 documenti leggeri.
 */
export async function listKbContents(): Promise<KbContenuto[]> {
  const snap = await getDocs(collection(db, CONTENTS_COLLECTION));
  const contenuti = snap.docs.map((d) => mapSnap<KbContenuto>(d));
  contenuti.sort(ordinaPerRecenza);
  return contenuti;
}

/** Dal più recente al più vecchio; a parità di data, per titolo. */
function ordinaPerRecenza(a: KbContenuto, b: KbContenuto): number {
  const ma = aMillis(a.ordinamento_at) ?? aMillis(a.updated_at) ?? aMillis(a.created_at) ?? 0;
  const mb = aMillis(b.ordinamento_at) ?? aMillis(b.updated_at) ?? aMillis(b.created_at) ?? 0;
  if (ma !== mb) return mb - ma;
  return (a.titolo ?? '').localeCompare(b.titolo ?? '', 'it', { sensitivity: 'base' });
}

/** Una singola testata, o `null` se lo slug non esiste. */
export async function getKbContent(contentId: string): Promise<KbContenuto | null> {
  const snap = await getDoc(doc(db, CONTENTS_COLLECTION, contentId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as Record<string, unknown>), id: snap.id } as KbContenuto;
}

/**
 * Filtro in memoria di formato, stato e testo libero.
 * Funzione PURA: la lista che si vede è esattamente questa applicata al dato.
 */
export function filtraContenuti(
  contenuti: readonly KbContenuto[],
  filtri: KbFiltriContenuti,
): KbContenuto[] {
  const testo = filtri.testo.trim().toLowerCase();
  return contenuti.filter((c) => {
    if (filtri.formato !== KB_FILTRO_TUTTI && c.formato !== filtri.formato) return false;
    if (filtri.stato !== KB_FILTRO_TUTTI && c.stato !== filtri.stato) return false;
    if (!testo) return true;
    const campi = [c.titolo, c.id, c.riassunto].map((v) => (v ?? '').toLowerCase());
    return campi.some((v) => v.includes(testo));
  });
}

// ============================================================================
// REVISIONI — il corpo
// ============================================================================

/**
 * Storico completo delle revisioni, dalla più recente.
 * Ordina per `numero` (campo indicizzato d'ufficio): nessun indice composito.
 */
export async function listKbRevisioni(contentId: string): Promise<KbRevisioneConsole[]> {
  const snap = await getDocs(
    query(
      collection(db, CONTENTS_COLLECTION, contentId, REVISIONI_SUBCOLLECTION),
      orderBy('numero', 'desc'),
    ),
  );
  return snap.docs.map((d) => mapSnap<KbRevisioneConsole>(d));
}

/** Una revisione per numero (doc id zero-paddato), o `null` se non c'è. */
export async function getKbRevisione(
  contentId: string,
  numero: number,
): Promise<KbCorpoCaricato | null> {
  const snap = await getDoc(
    doc(db, CONTENTS_COLLECTION, contentId, REVISIONI_SUBCOLLECTION, idRevisione(numero)),
  );
  if (!snap.exists()) return null;
  const r = snap.data() as KbRevisione;
  return {
    numero: r.numero ?? numero,
    stato_revisione: r.stato_revisione,
    titolo_snapshot: r.titolo_snapshot ?? '',
    riassunto_snapshot: r.riassunto_snapshot ?? '',
    corpo_markdown: r.corpo_markdown ?? '',
  };
}

/**
 * Corpo da aprire nell'editor: la BOZZA se c'è, altrimenti la PUBBLICATA.
 *
 * L'ordine non è arbitrario: la bozza è il lavoro in corso dell'editore, e
 * riaprire la pubblicata gli farebbe perdere silenziosamente le modifiche
 * salvate ma non ancora messe in onda.
 * `null` = il contenuto non ha nessuna revisione (testata appena creata).
 */
export async function getCorpoDaModificare(
  contenuto: Pick<KbContenuto, 'id' | 'revisione_bozza' | 'revisione_pubblicata'>,
): Promise<KbCorpoCaricato | null> {
  const numero = contenuto.revisione_bozza ?? contenuto.revisione_pubblicata;
  if (typeof numero !== 'number' || numero < 1) return null;
  return getKbRevisione(contenuto.id, numero);
}

/** Corpo della revisione PUBBLICATA: è il «prima» del confronto in pubblicazione. */
export async function getCorpoPubblicato(
  contenuto: Pick<KbContenuto, 'id' | 'revisione_pubblicata'>,
): Promise<KbCorpoCaricato | null> {
  const numero = contenuto.revisione_pubblicata;
  if (typeof numero !== 'number' || numero < 1) return null;
  return getKbRevisione(contenuto.id, numero);
}

// ============================================================================
// AREE FUNZIONALI — vocabolario controllato
// ============================================================================

/**
 * Aree attive di `kb_areas`, ordinate per etichetta.
 *
 * ⚠️ Vocabolario CONTROLLATO, non testo libero: è la chiave di giunzione con i
 * rilasci futuri. Se l'editore potesse scrivere l'area a mano,
 * `noleggio-operativo` e `noleggio_operativo` convivrebbero e la regola
 * anti-obsoleti cercherebbe la prima trovando la seconda — cioè niente, in
 * silenzio. Un vocabolario vuoto è un problema da SEGNALARE (lo fa la UI), non
 * da aggirare riaprendo il testo libero.
 */
export async function listKbAreas(): Promise<KbArea[]> {
  const snap = await getDocs(collection(db, AREAS_COLLECTION));
  const aree = snap.docs
    .map((d) => mapSnap<KbArea>(d))
    .filter((a) => a.attivo !== false);
  aree.sort((a, b) =>
    (a.label ?? a.id).localeCompare(b.label ?? b.id, 'it', { sensitivity: 'base' }),
  );
  return aree;
}

// ============================================================================
// GOVERNANCE
// ============================================================================

/**
 * Comportamento per livello di impatto, letto dai DATI.
 *
 * Se `kb_settings/governance` non esiste, si restituiscono i valori di partenza
 * del modulo condiviso CON `origine: 'default'`: la console lo dichiara a
 * schermo. Restituirli spacciandoli per configurazione sarebbe il fallback
 * silenzioso che il progetto vieta.
 */
export async function getKbGovernance(): Promise<KbGovernanceCaricata> {
  const snap = await getDoc(doc(db, SETTINGS_COLLECTION, GOVERNANCE_DOC));
  if (!snap.exists()) {
    return { governance: KB_GOVERNANCE_DEFAULT, origine: 'default' };
  }
  const dati = snap.data() as Partial<KbGovernance>;
  // Fusione superficiale sui soli campi noti: un documento parziale (scritto a
  // mano o da un seed vecchio) non deve produrre `undefined` dove il codice
  // legge un numero.
  const governance: KbGovernance = {
    giorni_validita_default:
      dati.giorni_validita_default ?? KB_GOVERNANCE_DEFAULT.giorni_validita_default,
    widget_max_novita: dati.widget_max_novita ?? KB_GOVERNANCE_DEFAULT.widget_max_novita,
    widget_mostra_se_vuoto:
      dati.widget_mostra_se_vuoto ?? KB_GOVERNANCE_DEFAULT.widget_mostra_se_vuoto,
    livelli_impatto: {
      ...KB_GOVERNANCE_DEFAULT.livelli_impatto,
      ...(dati.livelli_impatto ?? {}),
    },
  };
  return { governance, origine: 'firestore' };
}

// ============================================================================
// PLATEA — utenti e aziende, per l'anteprima destinatari e la scelta dell'owner
// ============================================================================

/** Stati account che NON ricevono: vocabolario storico misto italiano/inglese. */
const STATI_NON_OPERATIVI = new Set(['disabilitato', 'disabled', 'sospeso', 'suspended']);

/**
 * Tutti gli utenti e tutte le aziende.
 *
 * Il ruolo di ciascun utente si risolve sul suo tenant PRIMARIO con
 * `risolviRuoloSulTenant` (modulo condiviso): la regola di «quale ruolo vale su
 * quale azienda» ha una sola implementazione nel repo e questa non è una
 * seconda. Conseguenza dichiarata: chi collabora con più aziende viene contato
 * una volta sola, sulla propria azienda principale — un conteggio di PERSONE,
 * non di rapporti di lavoro.
 */
export async function caricaPlatea(): Promise<KbPlatea> {
  const [usersSnap, tenants] = await Promise.all([
    getDocs(collection(db, APP_USERS_COLLECTION)),
    listTenants({ max: 500 }),
  ]);

  const utenti: KbUtenteConsole[] = usersSnap.docs.map((d) => {
    const dati = d.data() as Record<string, unknown>;
    const tenantId = testoNormalizzato(dati.azienda_id) || null;
    const ruoli = tenantId
      ? risolviRuoloSulTenant(dati as UtenteTenantLike, tenantId).roles
      : [];
    const nome =
      testoNormalizzato(dati.display_name) ||
      testoNormalizzato(dati.nome_completo) ||
      testoNormalizzato(dati.nome);
    const email = testoNormalizzato(dati.email) || null;
    const stato = testoNormalizzato(dati.status).toLowerCase();
    return {
      uid: d.id,
      etichetta: nome || email || d.id,
      email,
      tenant_id: tenantId,
      ruoli,
      operativo: !STATI_NON_OPERATIVI.has(stato),
    };
  });

  utenti.sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }));
  return { utenti, tenants };
}

/**
 * Ruoli realmente presenti nella base utenti, ordinati.
 *
 * Il registro dei ruoli vive in `src/core/rbac` dell'app tenant e la console non
 * può importarlo (bundle separati); duplicarlo qui vorrebbe dire mantenere due
 * elenchi. Si derivano quindi dai DATI — che ha anche il pregio di non offrire
 * come bersaglio un ruolo che nessuno ha. `giaSelezionati` viene unito perché un
 * ruolo salvato su un contenuto non deve sparire dalla tendina se oggi nessun
 * utente lo ha.
 */
export function ruoliDisponibili(
  utenti: readonly KbUtenteConsole[],
  giaSelezionati: readonly string[] = [],
): string[] {
  const insieme = new Set<string>();
  for (const u of utenti) for (const r of u.ruoli) if (r) insieme.add(r);
  for (const r of giaSelezionati) if (r) insieme.add(r);
  return Array.from(insieme).sort((a, b) => a.localeCompare(b, 'it', { sensitivity: 'base' }));
}

/**
 * Capability citate nei dati dei tenant (base/addon/override/expired), ordinate.
 * Alimenta i suggerimenti dei due campi capability senza scrivere in codice un
 * solo id: il motore non deve sapere cosa sia il noleggio operativo.
 */
export function capabilityDisponibili(
  tenants: readonly KbTenantConsole[],
  giaUsate: readonly string[] = [],
): string[] {
  const insieme = new Set<string>();
  for (const t of tenants) {
    const m = t.modules;
    for (const elenco of [m?.base, m?.addon, m?.override, m?.expired]) {
      for (const id of elenco ?? []) if (id) insieme.add(id);
    }
  }
  for (const id of giaUsate) if (id) insieme.add(id);
  return Array.from(insieme).sort();
}

/**
 * «Questo contenuto raggiungerà N persone su M aziende.»
 *
 * Funzione PURA (nessun I/O): decide con `contaDestinatari` di `shared/kb`, cioè
 * con lo STESSO filtro che userà il widget in home. Se qui il numero fosse
 * calcolato a modo nostro, l'editore pubblicherebbe per un pubblico e ne
 * raggiungerebbe un altro.
 *
 * Le capability per tenant si risolvono con `hasCapability` (unica fonte
 * canonica) e SOLO per le capability effettivamente citate dal contenuto: non si
 * ricostruisce l'insieme di tutte le capability attive di un'azienda, che
 * sarebbe una seconda implementazione della stessa regola.
 *
 * ⚠️ LIMITE DICHIARATO: le capability vendute come app add-on per POSTO
 * (`app_users.seat_apps`) qui si valutano allo stato del TENANT, perché il
 * predicato per-posto richiede l'insieme `app_addons` e il documento del singolo
 * utente. Il numero può quindi essere generoso per un annuncio mirato su una di
 * quelle: è un'anteprima, non un contatore di consegne.
 */
export function calcolaAnteprimaDestinatari(
  contenuto: KbContenuto,
  platea: KbPlatea,
): KbAnteprimaDestinatari {
  const capabilityCitate = [
    contenuto.visibilita_tenant?.capability_richiesta,
    contenuto.visibilita_tenant?.capability_informativa,
  ].filter((c): c is string => typeof c === 'string' && c.trim().length > 0);

  const capabilityPerTenant: Record<string, string[]> = {};
  for (const t of platea.tenants) {
    capabilityPerTenant[t.id] = capabilityCitate.filter((c) => hasCapability(t, c));
  }

  const operativi = platea.utenti.filter((u) => u.operativo);
  const esito = contaDestinatari(
    contenuto,
    operativi.map((u) => ({ uid: u.uid, tenant_id: u.tenant_id, ruoli: u.ruoli })),
    capabilityPerTenant,
  );

  return {
    totale: esito.totale,
    per_tenant: esito.per_tenant,
    aziende_raggiunte: Object.keys(esito.per_tenant).length,
    aziende_totali: platea.tenants.length,
    utenti_valutati: operativi.length,
    utenti_non_operativi: platea.utenti.length - operativi.length,
  };
}

// ============================================================================
// SCRITTURE — solo via callable
// ============================================================================

/**
 * Salva la bozza (crea il contenuto la prima volta).
 *
 * Il corpo NON può essere scritto dal client: `kb_revisioni` ha
 * `allow write: if false`. Questa callable è quindi l'unica strada, non una
 * scelta di comodo.
 */
export async function salvaBozzaKbContenuto(
  input: SalvaBozzaKbRequest,
): Promise<SalvaBozzaKbResponse> {
  const fn = httpsCallable<SalvaBozzaKbRequest, SalvaBozzaKbResponse>(
    functions,
    'salvaBozzaKbContenuto',
  );
  const payload = stripUndefined({
    content_id: input.content_id,
    contenuto: stripUndefined(input.contenuto as Record<string, unknown>),
    corpo_markdown: input.corpo_markdown,
  }) as unknown as SalvaBozzaKbRequest;
  const esito = await fn(payload);
  return esito.data;
}

/**
 * Pubblica: unica strada verso lo stato `pubblicato`.
 * `tipo_modifica` e `motivo` li decide l'umano e restano nel dato.
 */
export async function pubblicaKbContenuto(
  input: PubblicaKbRequest,
): Promise<PubblicaKbResponse> {
  const fn = httpsCallable<PubblicaKbRequest, PubblicaKbResponse>(
    functions,
    'pubblicaKbContenuto',
  );
  const esito = await fn(input);
  return esito.data;
}

/** Ritira un contenuto dalla circolazione (resta nell'archivio e nello storico). */
export async function archiviaKbContenuto(
  input: ArchiviaKbRequest,
): Promise<ArchiviaKbResponse> {
  const fn = httpsCallable<ArchiviaKbRequest, ArchiviaKbResponse>(
    functions,
    'archiviaKbContenuto',
  );
  const esito = await fn(input);
  return esito.data;
}

// ============================================================================
// IMMAGINI DEL CORPO — Storage
// ============================================================================

/** Nome file sicuro per Storage (stessa regola dei video). */
export function sanitizeFileName(name: string): string {
  return name.replace(/[^\w.\-]+/g, '_');
}

/** Percorso canonico di un'immagine del corpo: `kb_media/{content_id}/{file}`. */
export function buildKbMediaStoragePath(contentId: string, fileName: string): string {
  return `${STORAGE_ROOT}/${contentId}/${sanitizeFileName(fileName)}`;
}

/**
 * Avvia l'upload resumable dell'immagine (stessa forma dei video: il chiamante
 * osserva il progresso con `task.on('state_changed', …)` e può annullare).
 */
export function uploadKbImmagine(
  contentId: string,
  file: File,
): { task: UploadTask; storagePath: string } {
  const storagePath = buildKbMediaStoragePath(contentId, file.name);
  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type || 'application/octet-stream',
  });
  return { task, storagePath };
}

/** URL di download/visualizzazione di un file in `kb_media`. */
export async function getKbMediaDownloadUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

/**
 * Frammento markdown di un'immagine.
 *
 * Nel testo va l'URL, non il percorso Storage: un renderer markdown qualunque
 * sa risolvere un URL e non sa nulla di Firebase. Il percorso resta esposto
 * all'operatore per la tracciabilità del file caricato.
 */
export function markdownImmagine(url: string, testoAlternativo: string): string {
  const alt = testoAlternativo.replace(/[[\]]/g, '').trim() || 'immagine';
  return `![${alt}](${url})`;
}

/**
 * Carica un'immagine e restituisce ciò che serve per incollarla nel corpo.
 * Attende il completamento: senza URL non c'è nulla da inserire nel markdown.
 */
export async function caricaImmagineCorpo(
  contentId: string,
  file: File,
  onProgress?: (percentuale: number) => void,
): Promise<KbImmagineCaricata> {
  const { task, storagePath } = uploadKbImmagine(contentId, file);
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (onProgress) {
          onProgress(snap.totalBytes > 0 ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0);
        }
      },
      (err) => reject(err),
      () => resolve(),
    );
  });
  const url = await getKbMediaDownloadUrl(storagePath);
  const nomeSenzaEstensione = file.name.replace(/\.[^.]+$/, '');
  return { storage_path: storagePath, url, markdown: markdownImmagine(url, nomeSenzaEstensione) };
}

// ============================================================================
// MANUTENZIONE — viste che DEVONO restare vuote
// ============================================================================

/**
 * Contenuti senza responsabile. Nessuna architettura garantisce che qualcuno
 * scriva o aggiorni un articolo: la sola difesa è che questa lista resti vuota.
 */
export function contenutiSenzaOwner(contenuti: readonly KbContenuto[]): KbContenuto[] {
  return contenuti.filter((c) => c.stato !== 'archiviato' && !testoNormalizzato(c.owner_uid));
}

/**
 * Contenuti con la revisione periodica scaduta.
 *
 * `revisione_scadenza_at` è DERIVATO dal trigger a partire da
 * `revisione_ogni_giorni`: se il campo è assente il contenuto non è «in
 * regola», è semplicemente senza scadenza dichiarata — e comparirà in questa
 * lista solo quando qualcuno gliene darà una. Distinzione voluta: questa vista
 * non deve inventare scadenze.
 */
export function contenutiScaduti(
  contenuti: readonly KbContenuto[],
  adessoMs: number,
): KbContenuto[] {
  return contenuti
    .filter((c) => c.stato === 'pubblicato')
    .filter((c) => {
      const scadenza = aMillis(c.revisione_scadenza_at);
      return scadenza != null && scadenza <= adessoMs;
    });
}

/**
 * Formati che l'editor della console gestisce in fase 1.
 *
 * Sottoinsieme DELIBERATO di `KB_FORMATI`: il formato `video` resta alla tab
 * «video» e alla sua collezione in produzione (`platform_docs_*`) finché non
 * verrà assorbito (fase 4). Non è un vocabolario nuovo, è una restrizione di
 * questa schermata — per questo vive qui e non in `shared/`.
 */
export const KB_FORMATI_EDITOR: readonly KbFormato[] = ['novita', 'articolo'] as const;
